import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import { useDOA } from '../hooks/useDOA';
import { useEntities } from '../hooks/useEntities';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import FilterBar from '../components/ui/FilterBar';

const API = `${API_V1}/fixed-assets`;
const PAGE_SIZE = 50;

const ASSET_CLASSES = ['BUILDING', 'VEHICLE', 'EQUIPMENT', 'FURNITURE', 'IT_HARDWARE', 'IT_SOFTWARE', 'LEASEHOLD', 'LAND', 'CIP'] as const;
const DEP_METHODS = ['STRAIGHT_LINE', 'DECLINING_BALANCE', 'UNITS_OF_PRODUCTION'] as const;
const STATUSES = ['DRAFT', 'ACTIVE', 'FULLY_DEPRECIATED', 'DISPOSED', 'IMPAIRED'] as const;

interface Asset {
  id: number;
  asset_id: string;
  description: string;
  asset_class: string;
  entity_code: string;
  acquisition_date: string;
  acquisition_cost: number;
  salvage_value: number;
  useful_life_months: number;
  depreciation_method: string;
  accumulated_depreciation: number;
  net_book_value: number;
  status: string;
  gl_account: string;
  depreciation_account: string;
  location: string;
  cost_center: string;
  disposal_date: string | null;
  disposal_proceeds: number | null;
  gain_loss: number | null;
  depreciation_details?: DepreciationDetail[];
}

interface DepreciationDetail {
  period: string;
  amount: number;
  cumulative: number;
  nbv_after: number;
}

interface Summary {
  total_assets: number;
  total_cost: number;
  total_nbv: number;
  fully_depreciated: number;
  disposed: number;
}

const emptyForm = {
  entity_code: '',
  asset_id: '',
  description: '',
  asset_class: '',
  acquisition_date: '',
  acquisition_cost: '',
  salvage_value: '0',
  useful_life_months: '',
  depreciation_method: 'STRAIGHT_LINE',
  gl_account: '',
  depreciation_account: '',
  location: '',
  cost_center: '',
};

export default function FixedAssets() {
  const { canApprove } = useDOA();
  const entities = useEntities();
  const { entity } = useGlobalEntity();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showDispose, setShowDispose] = useState<Asset | null>(null);
  const [showDepRun, setShowDepRun] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({ ...emptyForm });

  // Disposal form
  const [disposeDate, setDisposeDate] = useState('');
  const [disposeProceeds, setDisposeProceeds] = useState('');

  // Depreciation run form
  const [depPeriod, setDepPeriod] = useState('');
  const [depEntity, setDepEntity] = useState('');
  const [depResult, setDepResult] = useState<{ assets_processed: number; total_depreciation: number; jes_posted: number } | null>(null);
  const [depRunning, setDepRunning] = useState(false);

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (statusFilter) p.set('status', statusFilter);
    // Local entityFilter takes precedence; fall back to global entity selector
    const effectiveEntity = entityFilter || entity;
    if (entityFilter) p.set('entity_code', entityFilter);
    if (classFilter) p.set('asset_class', classFilter);
    try {
      const listUrl = entityFilter ? `${API}?${p}` : withEntityParam(`${API}?${p}`, entity);
      const [listRes, sumRes] = await Promise.all([
        authFetch(listUrl),
        authFetch(withEntityParam(`${API}/summary`, effectiveEntity)),
      ]);
      if (listRes.ok) { const d = await listRes.json(); setAssets(d.assets || d.items || []); setTotal(d.total ?? (d.assets || d.items || []).length); }
      if (sumRes.ok) { setSummary(await sumRes.json()); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter, entityFilter, classFilter, entity]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      const payload = {
        ...form,
        acquisition_cost: parseFloat(form.acquisition_cost) || 0,
        salvage_value: parseFloat(form.salvage_value) || 0,
        useful_life_months: parseInt(form.useful_life_months) || 0,
      };
      const res = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) { showToast('Asset created', true); setShowCreate(false); setForm({ ...emptyForm }); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error creating asset', false); }
    } catch { showToast('Failed to create asset', false); }
  };

  const handleActivate = async (id: number) => {
    try {
      const res = await authFetch(`${API}/${id}/activate`, { method: 'POST' });
      if (res.ok) { showToast('Asset activated', true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Activation failed', false); }
  };

  const handleImpair = async (id: number) => {
    const amount = prompt('Enter impairment amount:');
    if (!amount) return;
    try {
      const res = await authFetch(`${API}/${id}/impair`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ impairment_amount: parseFloat(amount) }) });
      if (res.ok) { showToast('Impairment recorded', true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Impairment failed', false); }
  };

  const handleDispose = async () => {
    if (!showDispose) return;
    try {
      const res = await authFetch(`${API}/${showDispose.id}/dispose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ disposal_date: disposeDate, proceeds: parseFloat(disposeProceeds) || 0 }),
      });
      if (res.ok) { showToast('Asset disposed', true); setShowDispose(null); setDisposeDate(''); setDisposeProceeds(''); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Disposal failed', false); }
  };

  const handleRunDepreciation = async () => {
    setDepRunning(true);
    setDepResult(null);
    try {
      const res = await authFetch(`${API}/run-depreciation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period: depPeriod, entity_code: depEntity || undefined }),
      });
      if (res.ok) { const d = await res.json(); setDepResult(d); showToast('Depreciation run complete', true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Depreciation run failed', false); }
    setDepRunning(false);
  };

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Compute gain/loss preview for disposal
  const disposeGainLoss = showDispose ? (parseFloat(disposeProceeds) || 0) - showDispose.net_book_value : 0;

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const assetColumns = [
    { key: 'asset_id', label: 'Asset ID', render: (v: any) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'description', label: 'Description', render: (v: any, a: any) => (
      <div>
        <div>{v || a.asset_description || '-'}</div>
        {a.source_system && (
          <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 2 }}>
            from {a.source_system.replace(':', ' ').replace('#', ' line ')}
          </div>
        )}
      </div>
    ) },
    { key: 'asset_class', label: 'Class', render: (v: any) => <span style={{ color: 'var(--text-secondary)' }}>{v?.replace(/_/g, ' ')}</span> },
    { key: 'acquisition_date', label: 'Acq Date', render: (v: any) => <span style={{ color: 'var(--text-secondary)' }}>{v}</span> },
    { key: 'acquisition_cost', label: 'Cost', align: 'right' as const, render: (v: any) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'accumulated_depreciation', label: 'Accum Dep', align: 'right' as const, render: (v: any) => <span style={{ color: 'var(--negative)' }}>{formatAmount(v)}</span> },
    { key: 'net_book_value', label: 'NBV', align: 'right' as const, render: (v: any) => <span style={{ fontWeight: 600, color: 'var(--positive)' }}>{formatAmount(v)}</span> },
    { key: 'depreciation_method', label: 'Method', render: (v: any) => <span style={{ color: 'var(--text-secondary)', fontSize: 10 }}>{v?.replace(/_/g, ' ')}</span> },
    { key: 'status', label: 'Status', render: (v: any) => <StatusBadge status={v} /> },
    { key: 'actions', label: 'Actions', render: (_: any, a: Asset) => (
      <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
        {a.status === 'DRAFT' && <ActionButton label="Activate" onClick={(e) => { e.stopPropagation(); handleActivate(a.id); }} variant="success" size="sm" />}
        {a.status === 'ACTIVE' && (
          <>
            <ActionButton label="Dispose" onClick={(e) => { e.stopPropagation(); setDisposeDate(''); setDisposeProceeds(''); setShowDispose(a); }} variant="secondary" size="sm" />
            <ActionButton label="Impair" onClick={(e) => { e.stopPropagation(); handleImpair(a.id); }} variant="danger" size="sm" />
          </>
        )}
        {a.status === 'FULLY_DEPRECIATED' && <ActionButton label="Dispose" onClick={(e) => { e.stopPropagation(); setDisposeDate(''); setDisposeProceeds(''); setShowDispose(a); }} variant="secondary" size="sm" />}
      </div>
    )},
  ];

  const depScheduleColumns = [
    { key: 'period', label: 'Period' },
    { key: 'amount', label: 'Amount', align: 'right' as const, render: (v: any) => formatAmount(v) },
    { key: 'cumulative', label: 'Cumulative', align: 'right' as const, render: (v: any) => formatAmount(v) },
    { key: 'nbv_after', label: 'NBV After', align: 'right' as const, render: (v: any) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
  ];

  return (
    <PageLayout
      title="Fixed Assets"
      subtitle="Track, depreciate, and manage capital assets across entities"
      actions={[
        { label: 'Run Depreciation', onClick: () => { setDepResult(null); setShowDepRun(true); }, variant: 'secondary' },
        { label: '+ New Asset', onClick: () => { setForm({ ...emptyForm }); setShowCreate(true); } },
      ]}
    >
      {summary && (
        <KpiGrid items={[
          { label: 'Total Assets', value: summary.total_assets },
          { label: 'Total Cost', value: formatAmountCompact(summary.total_cost), color: 'var(--accent)' },
          { label: 'Total NBV', value: formatAmountCompact(summary.total_nbv), color: 'var(--positive)' },
          { label: 'Fully Depreciated', value: summary.fully_depreciated, color: 'var(--warning)' },
          { label: 'Disposed', value: summary.disposed, color: 'var(--text-secondary)' },
        ]} />
      )}

      <FilterBar filters={[
        { key: 'entity', label: 'Entity', type: 'select', value: entityFilter, onChange: (v) => { setEntityFilter(v); setPage(0); }, options: entities.map(e => ({ value: e.entity_code, label: `${e.entity_code} - ${e.entity_name}` })), width: 200 },
        { key: 'class', label: 'Class', type: 'select', value: classFilter, onChange: (v) => { setClassFilter(v); setPage(0); }, options: ASSET_CLASSES.map(c => ({ value: c, label: c.replace(/_/g, ' ') })), width: 180 },
        { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: (v) => { setStatusFilter(v); setPage(0); }, options: STATUSES.map(s => ({ value: s, label: s.replace(/_/g, ' ') })), width: 200 },
      ]} />

      {/* Asset table with expandable rows */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
        <thead>
          <tr>
            {assetColumns.map(col => (
              <th key={col.key} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', borderBottom: '2px solid var(--border-subtle)', background: 'var(--bg-card)', textAlign: col.align || 'left', whiteSpace: 'nowrap' }}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={assetColumns.length} style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>Loading...</td></tr>
          ) : assets.length === 0 ? (
            <tr><td colSpan={assetColumns.length} style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>No fixed assets found.</td></tr>
          ) : assets.map(a => (
            <>
              <tr key={a.id} onClick={() => toggleExpand(a.id)} style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', background: expandedId === a.id ? 'var(--accent-subtle, #f0f4f8)' : 'transparent' }}>
                {assetColumns.map(col => (
                  <td key={col.key} style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)', textAlign: col.align || 'left' }}>
                    {col.render ? col.render((a as any)[col.key], a) : (a as any)[col.key] ?? '-'}
                  </td>
                ))}
              </tr>
              {expandedId === a.id && (
                <tr key={`${a.id}-details`}>
                  <td colSpan={assetColumns.length} style={{ padding: '0 14px 12px 32px', background: 'var(--accent-subtle, #f0f4f8)' }}>
                    <div style={{ display: 'flex', gap: 24, marginTop: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                      {[
                        { label: 'Entity', value: a.entity_code },
                        { label: 'GL Account', value: a.gl_account || '-' },
                        { label: 'Dep Account', value: a.depreciation_account || '-' },
                        { label: 'Location', value: a.location || '-' },
                        { label: 'Cost Center', value: a.cost_center || '-' },
                        { label: 'Salvage Value', value: formatAmount(a.salvage_value) },
                        { label: 'Useful Life', value: `${a.useful_life_months} months` },
                      ].map(item => (
                        <div key={item.label}>
                          <span style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const, fontWeight: 700 }}>{item.label}</span>
                          <div style={{ fontSize: 12, fontWeight: item.label === 'Entity' ? 600 : 400 }}>{item.value}</div>
                        </div>
                      ))}
                      {a.disposal_date && (
                        <>
                          <div>
                            <span style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const, fontWeight: 700 }}>Disposal Date</span>
                            <div style={{ fontSize: 12 }}>{a.disposal_date}</div>
                          </div>
                          <div>
                            <span style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const, fontWeight: 700 }}>Proceeds</span>
                            <div style={{ fontSize: 12 }}>{formatAmount(a.disposal_proceeds || 0)}</div>
                          </div>
                          <div>
                            <span style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const, fontWeight: 700 }}>Gain/Loss</span>
                            <div style={{ fontSize: 12, color: (a.gain_loss || 0) >= 0 ? 'var(--positive)' : 'var(--negative)' }}>{formatAmount(a.gain_loss || 0)}</div>
                          </div>
                        </>
                      )}
                    </div>
                    {a.depreciation_details && a.depreciation_details.length > 0 && (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginTop: 8, marginBottom: 6 }}>DEPRECIATION SCHEDULE</div>
                        <DataTable
                          columns={depScheduleColumns}
                          rows={a.depreciation_details}
                          emptyMessage="No depreciation records."
                        />
                      </>
                    )}
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
          <ActionButton label="PREV" onClick={() => setPage(Math.max(0, page - 1))} variant="secondary" size="sm" disabled={page === 0} />
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: '28px' }}>{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
          <ActionButton label="NEXT" onClick={() => setPage(page + 1)} variant="secondary" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} />
        </div>
      )}

      {/* Create Asset Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Fixed Asset" size="lg" footer={
        <><ActionButton label="Cancel" onClick={() => setShowCreate(false)} variant="secondary" /><ActionButton label="Create Asset" onClick={handleCreate} /></>
      }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <select value={form.entity_code} onChange={e => setForm({ ...form, entity_code: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select Legal Entity *</option>
            {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} - {e.entity_name} ({e.functional_currency})</option>)}
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Asset ID *" value={form.asset_id} onChange={e => setForm({ ...form, asset_id: e.target.value })} style={inputStyle} />
            <input placeholder="Description *" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...inputStyle, flex: 2 }} />
          </div>
          <select value={form.asset_class} onChange={e => setForm({ ...form, asset_class: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select Asset Class *</option>
            {ASSET_CLASSES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Acquisition Date *</label>
              <input type="date" value={form.acquisition_date} onChange={e => setForm({ ...form, acquisition_date: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Acquisition Cost *</label>
              <input type="number" placeholder="0.00" value={form.acquisition_cost} onChange={e => setForm({ ...form, acquisition_cost: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Salvage Value</label>
              <input type="number" placeholder="0.00" value={form.salvage_value} onChange={e => setForm({ ...form, salvage_value: e.target.value })} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Useful Life (months) *</label>
              <input type="number" placeholder="e.g. 60" value={form.useful_life_months} onChange={e => setForm({ ...form, useful_life_months: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 10, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Depreciation Method *</label>
              <select value={form.depreciation_method} onChange={e => setForm({ ...form, depreciation_method: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
                {DEP_METHODS.map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="GL Account" value={form.gl_account} onChange={e => setForm({ ...form, gl_account: e.target.value })} style={inputStyle} />
            <input placeholder="Depreciation Account" value={form.depreciation_account} onChange={e => setForm({ ...form, depreciation_account: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="Location" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} style={inputStyle} />
            <input placeholder="Cost Center" value={form.cost_center} onChange={e => setForm({ ...form, cost_center: e.target.value })} style={inputStyle} />
          </div>
        </div>
      </Modal>

      {/* Disposal Modal */}
      <Modal open={!!showDispose} onClose={() => setShowDispose(null)} title={`Dispose Asset: ${showDispose?.asset_id || ''}`} size="md" footer={
        <><ActionButton label="Cancel" onClick={() => setShowDispose(null)} variant="secondary" /><ActionButton label="Confirm Disposal" onClick={handleDispose} variant="primary" /></>
      }>
        {showDispose && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 16, padding: '12px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const, fontWeight: 700 }}>Cost</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{formatAmount(showDispose.acquisition_cost)}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const, fontWeight: 700 }}>Accum Dep</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--negative)' }}>{formatAmount(showDispose.accumulated_depreciation)}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const, fontWeight: 700 }}>NBV</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--positive)' }}>{formatAmount(showDispose.net_book_value)}</div>
              </div>
            </div>
            <div>
              <label style={{ fontSize: 10, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Disposal Date *</label>
              <input type="date" value={disposeDate} onChange={e => setDisposeDate(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 10, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Proceeds Amount</label>
              <input type="number" placeholder="0.00" value={disposeProceeds} onChange={e => setDisposeProceeds(e.target.value)} style={inputStyle} />
            </div>
            {/* Gain/Loss Preview */}
            <div style={{ padding: '12px', borderRadius: 6, border: `1px solid ${disposeGainLoss >= 0 ? 'var(--positive)' : 'var(--negative)'}`, background: disposeGainLoss >= 0 ? 'var(--positive-bg, #f0fdf4)' : 'var(--negative-bg, #fef2f2)' }}>
              <div style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const, fontWeight: 700, marginBottom: 2 }}>
                {disposeGainLoss >= 0 ? 'Estimated Gain on Disposal' : 'Estimated Loss on Disposal'}
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: disposeGainLoss >= 0 ? 'var(--positive)' : 'var(--negative)' }}>
                {formatAmount(Math.abs(disposeGainLoss))}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 4 }}>
                Proceeds ({formatAmount(parseFloat(disposeProceeds) || 0)}) - NBV ({formatAmount(showDispose.net_book_value)})
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Run Depreciation Modal */}
      <Modal open={showDepRun} onClose={() => setShowDepRun(false)} title="Run Depreciation" size="md" footer={
        <>
          <ActionButton label="Close" onClick={() => setShowDepRun(false)} variant="secondary" />
          {!depResult && (
            <ActionButton label={depRunning ? 'Running...' : 'Run Depreciation'} onClick={handleRunDepreciation} disabled={!depPeriod || depRunning} />
          )}
        </>
      }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 10, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Period (YYYY-MM) *</label>
            <input type="month" value={depPeriod} onChange={e => setDepPeriod(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 10, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Entity (optional -- blank = all entities)</label>
            <select value={depEntity} onChange={e => setDepEntity(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="">All Entities</option>
              {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} - {e.entity_name}</option>)}
            </select>
          </div>
          {depResult && (
            <div style={{ padding: '16px', borderRadius: 8, border: '1px solid var(--positive)', background: 'var(--positive-bg, #f0fdf4)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--positive)', marginBottom: 10 }}>Depreciation Run Complete</div>
              <KpiGrid items={[
                { label: 'Assets Processed', value: depResult.assets_processed },
                { label: 'Total Depreciation', value: formatAmountCompact(depResult.total_depreciation), color: 'var(--accent)' },
                { label: 'JEs Posted', value: depResult.jes_posted, color: 'var(--positive)' },
              ]} columns={3} />
            </div>
          )}
        </div>
      </Modal>

      {/* Toast */}
      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
